import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import type { Editor } from '@tiptap/core'
import { useSetAtom, useStore } from 'jotai'
import { twMerge } from 'tailwind-merge'
import { ACTION_HEADER_INDENT_CLASS } from '../../../components/action-header'
import { Mic, PenLine } from 'lucide-react'
import { Cell, Logo, type MarkdownFeatures } from '@highlight/ui'
import { FadingScrollable, type FadingScrollableBottomChrome } from '@renderer/chat'
import { HighlightAppIcon } from '@renderer/components/Icons/HighlightAppIcon'
import { NestedView } from '@renderer/components/nested-view'
import { ContentFrame, canRenderContentFrame } from '@renderer/components/content-frame'
import { CommenterMeta } from '@renderer/components/commenter-meta'
import { ActionTagArea } from '@renderer/components/action-tags'
import { applyMetadataTransitions, visibleNestedViews } from '@renderer/components/action-tags/status-transition'
import { useViewBodyBottomChrome, useViewBodyBottomInset } from '../../../components/view'
import { useViewEntryScrollPersistence } from '../../../hooks/use-view-scroll-persistence'
import { StagedActionMarkdown, useClampedOverflow } from '@renderer/chat/integrations/staged-action'
import { ReportExpandToggle, isReportToUserStagedAction } from '@renderer/chat/integrations/report-to-user'
import { useStagedActionEditOptions } from '../../../hooks/use-staged-actions'
import { useActionMarkdownConfig } from '../../../hooks/use-action-markdown-features'
import { useUpdateStagedActionView } from '../../../hooks/use-update-staged-action-view'
import { ActionReference } from '../../../components/action-reference'
import { discardDraftKeepingTitle, stageDraftContent } from '../inline-drafts'
import { actionInlineDraftsAtom } from '../inline-draft-state'
import { trackStagedActionCopy, trackStagedActionOpenLink } from '../../../hooks/staged-actions/track-staged-action'
import { focusWithCaretAtEnd } from '../caret'
import { useViewContext } from '../../../view-context'
import { useIsEntryOnTop, useUpdateTopViewContext } from '../../../view-atoms'
import type { ActionViewContext, ActionViewModel } from '../use-action-view-model'
import type { ActionDraftControl } from '../use-draft-action'
import { LearnedPreferenceNotice } from './learned-preference-notice'
import { isUndrafted as isUndraftedAction } from '../../meeting-view/action-items/send-readiness'

interface ActionViewBodyProps {
  model: ActionViewModel
  embedded?: 'card' | 'standalone'
  /** Whether the planner left the body blank, so the body says it isn't drafted yet. */
  draftControl?: ActionDraftControl
}

/**
 * Scrollable draft body: property chips, source references, nested views, and
 * the full content rendered directly on the surface (no card chrome, per the
 * HIG-5824 design). While the draft is still the user's (`isEditable` — a
 * failed invoke included, since fixing the draft by hand is the other half of
 * Retry) the content is click-to-type editable (HIG-5830): typing stages the
 * markdown in the inline draft, which overlays the server content until the
 * footer's Save persists it. While an edit regenerates, the whole body
 * de-emphasizes (fade + blur) and locks.
 *
 * Memoized: output-card focus toggles re-render the parent (the header's title
 * becomes editable), and the editor must not repaint under the click that
 * focuses it. The model is reference-stable across a focus change.
 */
export const ActionViewBody = memo(function ActionViewBody({ model, embedded, draftControl }: ActionViewBodyProps) {
  const { action, state, draft, isEditable } = model

  const scrollElRef = useRef<HTMLDivElement | null>(null)
  const [cardScrollNode, setCardScrollNode] = useState<HTMLDivElement | null>(null)
  const scrollRef = useCallback((node: HTMLDivElement | null | undefined) => {
    scrollElRef.current = node ?? null
  }, [])
  const cardScrollRef = useCallback(
    (node: HTMLDivElement | null | undefined) => {
      scrollRef(node)
      setCardScrollNode(node ?? null)
    },
    [scrollRef],
  )

  // Any card whose content outgrows its cap can lift it to be read in place (TRU-51).
  const isCard = embedded === 'card'
  const isReportCard = isCard && action != null && isReportToUserStagedAction(action)
  const [isCardExpanded, setIsCardExpanded] = useState(false)
  const cardOverflows = useClampedOverflow(cardScrollNode, isCard)
  const canExpandCard = isCard && (cardOverflows || isCardExpanded)
  // How far the card had scrolled when the user toggled it; unset outside a toggle.
  const pendingToggleScrollRef = useRef<number | null>(null)
  const toggleCardExpanded = () => {
    pendingToggleScrollRef.current = cardScrollNode?.scrollTop ?? 0
    setIsCardExpanded((expanded) => !expanded)
  }
  // Expanding unrolls the card from its top, so the view scrolls by what the
  // card had scrolled to keep the text being read in place. Collapsing can
  // leave the card above the viewport, so it's brought back into view. Only
  // the view's scroller moves: `scrollIntoView` would also scroll clipped ancestors.
  useLayoutEffect(() => {
    const cardScrollTop = pendingToggleScrollRef.current
    pendingToggleScrollRef.current = null
    const viewScroller = cardScrollNode && findScrollParent(cardScrollNode)
    if (cardScrollTop === null || !cardScrollNode || !viewScroller) {
      return
    }
    if (isCardExpanded) {
      viewScroller.scrollTop += cardScrollTop
    } else if (cardScrollNode.parentElement) {
      revealWithin(viewScroller, cardScrollNode.parentElement)
    }
  }, [isCardExpanded, cardScrollNode])

  const { editOptions, isLoading: isEditOptionsLoading } = useStagedActionEditOptions({
    actionId: isEditable ? action?.id : undefined,
  })
  const { updateView, isUpdating } = useUpdateStagedActionView()

  const markdownConfig = useActionMarkdownConfig(editOptions?.content?.markdown)
  const canEditContent = isEditable && markdownConfig != null

  const actionId = action?.id
  // Slack's view renders an empty body as "No content", which the editor would otherwise hold as the message
  const isUndrafted = !!action && isUndraftedAction(action)
  const serverContent = isUndrafted ? '' : action?.view?.content
  const setDrafts = useSetAtom(actionInlineDraftsAtom)
  const editorPristineRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    editorPristineRef.current = undefined
  }, [actionId])

  const handleContentChange = useCallback(
    (markdown: string, editor: { isFocused: boolean }) => {
      if (!actionId) return
      // Tiptap reports plugin-driven doc changes (e.g. StarterKit's trailing
      // node appending a paragraph on mount) through the same onChange as
      // typing. Remember that serialization as the editor's pristine form so
      // focusing — which often re-emits it — does not open Save. Only a
      // focused update that actually diverges is a user edit.
      if (!editor.isFocused) {
        editorPristineRef.current = markdown
        return
      }
      if (markdown === editorPristineRef.current) {
        return
      }
      setDrafts((drafts) => stageDraftContent(drafts, actionId, markdown, { content: serverContent }))
    },
    [actionId, serverContent, setDrafts],
  )

  // The empty space under the content reads as part of the document — a click
  // there (the scroll container itself, not any child) drops the caret at the
  // end of the editor, matching the title row's whole-block affordance. Reaches
  // for the contenteditable element rather than holding the editor; the
  // selector also gates on editability.
  const handleEmptyAreaClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    const editorEl = event.currentTarget.querySelector<HTMLElement>('.ProseMirror[contenteditable="true"]')
    if (editorEl) focusWithCaretAtEnd(editorEl)
  }, [])

  // Arriving from the hover card's edit affordance: land already editing, caret
  // at the end of the body. Handed to the editor rather than chased in the DOM —
  // Tiptap applies it at creation, whenever that happens.
  const { context, entryKey } = useViewContext<ActionViewContext>()

  // Leaving the entry abandons the body edit. User-driven leaves resolve the
  // draft through the leave guard's save/discard dialog before this runs, so
  // this discard is the safety net for programmatic navigations (sign-out
  // reset, the assistant's closeAllViews) where the entry is genuinely gone.
  // A teardown with the entry still on top is not a leave and keeps the
  // draft: StrictMode remounts, the action unloading, and the whole panel
  // closing (which unmounts the router) — so hiding the panel mid-edit
  // restores the edit on reopen. A staged title is always kept: the header's
  // unmount commit saves it and clears it from the atom itself.
  const store = useStore()
  const isEntryOnTop = useIsEntryOnTop()
  useEffect(() => {
    if (!actionId) return
    return () => {
      if (isEntryOnTop(entryKey)) return
      store.set(actionInlineDraftsAtom, (drafts) => discardDraftKeepingTitle(drafts, actionId))
    }
  }, [actionId, entryKey, isEntryOnTop, store])
  const shouldFocusContent = context?.focusContent === true && canEditContent

  // A one-shot intent, so it's cleared from the history entry the moment the
  // editor consumes it (in place, keeping the entry key and its persisted
  // scroll). Otherwise back/forward remounts this entry with the flag still set
  // and steals focus to the end of the body every time the user returns.
  const updateActionContext = useUpdateTopViewContext('action')
  const consumeFocusContent = useCallback(() => {
    // Land ready to type: caret after the last character, content scrolled to
    // its end. Applied directly rather than trusting Tiptap's creation-time
    // autofocus — this fires from the editor's own mount effect, so the DOM is
    // guaranteed to be there. The rAF retry re-pins the bottom after late
    // layout (images, nested views) the same way scroll restoration does.
    // Focusing is not an edit — Save stays down until the body actually changes.
    const scrollEl = scrollElRef.current
    const editorEl = scrollEl?.querySelector<HTMLElement>('.ProseMirror[contenteditable="true"]')
    if (editorEl) focusWithCaretAtEnd(editorEl)
    if (scrollEl) {
      scrollEl.scrollTop = scrollEl.scrollHeight
      requestAnimationFrame(() => {
        scrollEl.scrollTop = scrollEl.scrollHeight
      })
    }
    updateActionContext((current) => (current ? { ...current, focusContent: undefined } : current))
  }, [updateActionContext])

  if (!action) {
    return null
  }

  const view = action.view
  const hasTagsRow = (view?.tags.length ?? 0) > 0 || (isEditable && (view?.tagOrder?.length ?? 0) > 0)

  // An editable action mounts the editor even with an empty body — that's the
  // click-to-type surface, and the hover card's focusContent affordance has
  // nothing to focus without it. Read-only actions still render nothing when
  // there's no content to show.
  const hasContentEditor = Boolean(view && (view.content || canEditContent))

  const contentEditor =
    hasContentEditor && view ? (
      <ActionViewContent
        editorKey={`${action.id}-content-${markdownConfig != null}`}
        value={draft?.content ?? serverContent ?? ''}
        editable={canEditContent}
        onChange={canEditContent ? handleContentChange : undefined}
        features={markdownConfig?.features}
        maxLength={markdownConfig?.maxLength}
        autoFocus={shouldFocusContent ? 'end' : false}
        onAutoFocusApplied={consumeFocusContent}
        attachments={view.attachments}
      />
    ) : null

  const content = (
    <>
      {hasTagsRow && view && (
        <div className="mb-3">
          <ActionTagArea
            showEmpty
            tags={applyMetadataTransitions(view.tags, view.nestedViews)}
            tagOrder={view.tagOrder}
            editOptions={editOptions}
            isEditOptionsLoading={isEditOptionsLoading}
            isUpdating={isUpdating}
            canEdit={isEditable}
            onUpdate={(v) => updateView(action.id, v)}
            toolSet={action.toolSet}
            className={embedded ? '[&_[data-slot=badge]]:border-border' : undefined}
          />
        </div>
      )}
      {view &&
        visibleNestedViews(view.nestedViews ?? []).map((nested, idx) => (
          <NestedView
            key={idx}
            view={nested.view}
            onButtonAction={(target) =>
              target === 'open link'
                ? trackStagedActionOpenLink(action, { source: 'action-view', surface: 'nested-view' })
                : trackStagedActionCopy(action, 'link', { source: 'action-view', surface: 'nested-view' })
            }
          />
        ))}
      {action.references?.slice(1).map((ref, idx, arr) => {
        const isLastRef = idx === arr.length - 1 && !hasContentEditor
        return (
          <ActionReference key={`${ref.type}-${ref.title}-${idx}`}>
            <ActionReference.Trigger isLast={isLastRef}>
              <Cell.Icon size="small">
                {ref.type === 'livingDocument' ? (
                  <HighlightAppIcon size={12} />
                ) : ref.type === 'audioNote' ? (
                  <Mic />
                ) : ref.type === 'linear' ? (
                  <Logo name="linear-dark" size={12} />
                ) : ref.type === 'notion' ? (
                  <Logo name="notion" size={12} />
                ) : (
                  <Logo name="slack" />
                )}
              </Cell.Icon>
              <p className="line-clamp-1 min-w-0 flex-1">{ref.title}</p>
            </ActionReference.Trigger>
            {ref.reasoning && <ActionReference.Content>{ref.reasoning}</ActionReference.Content>}
          </ActionReference>
        )
      })}
      {draftControl?.canDraft && isEditable && !draft?.content?.trim() && <UndraftedNotice />}
      {contentEditor &&
        view &&
        // Mounting before edit options load briefly exposes unrestricted editor features; a body being drafted has no words yet.
        (draftControl?.isDrafting || (isEditOptionsLoading && isEditable) ? (
          <ActionContentSkeleton />
        ) : (
          <CommenterMeta contentMeta={view.contentMeta} contentClassName="pt-1">
            {canRenderContentFrame(view.contentFrame) ? (
              <ContentFrame frame={view.contentFrame}>{contentEditor}</ContentFrame>
            ) : (
              contentEditor
            )}
          </CommenterMeta>
        ))}

      <LearnedPreferenceNotice action={action} />
    </>
  )

  return (
    <div className={twMerge('relative flex min-h-0 flex-col', !embedded && 'flex-1')}>
      {embedded ? (
        embedded === 'standalone' ? (
          // A lone action uses the Output view's scroller; an inner cap leaves the rest of the view empty.
          <div
            ref={scrollRef}
            onClick={handleEmptyAreaClick}
            className={twMerge(
              canEditContent && 'cursor-text',
              state === 'updating' && 'pointer-events-none opacity-25',
            )}
          >
            <div data-staged-action-edit-box="">{content}</div>
          </div>
        ) : (
          <div
            className={twMerge(
              'pb-2',
              ACTION_HEADER_INDENT_CLASS,
              canEditContent && 'cursor-text',
              state === 'updating' && 'pointer-events-none opacity-25',
            )}
          >
            <div
              data-staged-action-edit-box=""
              className={twMerge(
                'corner-smooth group-data-[focused]/staged:border-blue-9 group-data-[focused]/staged:ring-blue-9 relative overflow-hidden rounded-2xl border transition group-data-[focused]/staged:ring-1',
                state !== 'sent' && 'shadow-sm',
              )}
            >
              <FadingScrollable
                ref={cardScrollRef}
                data-staged-action-body=""
                fadeSize="8%"
                // A deeper bottom fade so the text dissolves under the floating toggle.
                bottomChrome={canExpandCard && !isCardExpanded ? CARD_TOGGLE_FADE : undefined}
                onClick={handleEmptyAreaClick}
                className={twMerge(
                  'relative p-2.5',
                  !isCardExpanded && 'max-h-[calc(18rem-2px)]',
                  !isCardExpanded &&
                    (canEditContent || isReportCard) &&
                    'group-data-[focused]/staged:max-h-[calc(32rem-2px)]',
                )}
              >
                {content}
              </FadingScrollable>
              {canExpandCard && (
                <CardExpandToggle
                  isExpanded={isCardExpanded}
                  scrollNode={cardScrollNode}
                  onToggle={toggleCardExpanded}
                />
              )}
            </div>
          </div>
        )
      ) : (
        <ScrollableActionViewBody
          scrollRef={scrollRef}
          scrollElRef={scrollElRef}
          onClick={handleEmptyAreaClick}
          canEditContent={canEditContent}
          isUpdating={state === 'updating'}
        >
          {content}
        </ScrollableActionViewBody>
      )}
    </div>
  )
})

function findScrollParent(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement
  while (node) {
    const { overflowY } = getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node
    }
    node = node.parentElement
  }
  return null
}

/** Scrolls `scroller` the least that brings `element` into view, top first when it can't all fit. */
function revealWithin(scroller: HTMLElement, element: HTMLElement) {
  const view = scroller.getBoundingClientRect()
  const box = element.getBoundingClientRect()
  if (box.top < view.top) {
    scroller.scrollTop += box.top - view.top
  } else if (box.bottom > view.bottom) {
    scroller.scrollTop += Math.min(box.bottom - view.bottom, box.top - view.top)
  }
}

function canScrollFurther(node: HTMLElement, deltaY: number): boolean {
  return deltaY > 0 ? node.scrollTop + node.clientHeight < node.scrollHeight - 1 : node.scrollTop > 0
}

const CARD_TOGGLE_FADE: FadingScrollableBottomChrome = { inset: 0, fadeSize: 72 }

/**
 * A full-width strip around the toggle so the whole bottom edge is the hitbox.
 * Collapsed, it floats over the card's faded edge and blurs the text beneath
 * rather than covering it, and scrolls the card under it until the card can't
 * scroll further, then lets the view scroll.
 */
function CardExpandToggle({
  isExpanded,
  scrollNode,
  onToggle,
}: {
  isExpanded: boolean
  scrollNode: HTMLDivElement | null
  onToggle: () => void
}) {
  const stripRef = useRef<HTMLDivElement>(null)
  // Native and non-passive: React's wheel listener is passive, so the view would scroll along with the card.
  useEffect(() => {
    const strip = stripRef.current
    if (isExpanded || !strip || !scrollNode) {
      return
    }
    const scrollCard = (event: WheelEvent) => {
      if (!canScrollFurther(scrollNode, event.deltaY)) {
        return
      }
      event.preventDefault()
      scrollNode.scrollBy({ top: event.deltaY })
    }
    strip.addEventListener('wheel', scrollCard, { passive: false })
    return () => strip.removeEventListener('wheel', scrollCard)
  }, [isExpanded, scrollNode])

  return (
    <div
      ref={stripRef}
      data-card-expand-toggle=""
      onClick={(event) => {
        event.stopPropagation()
        onToggle()
      }}
      className={twMerge(
        'group/expand flex cursor-pointer justify-center',
        isExpanded ? 'pb-1.5' : 'absolute inset-x-0 bottom-0 h-12 items-end pb-1.5',
      )}
    >
      <ReportExpandToggle
        isExpanded={isExpanded}
        onToggle={onToggle}
        className={twMerge(
          'text-foreground-secondary group-hover/expand:text-foreground',
          !isExpanded && 'rounded-full backdrop-blur-sm',
        )}
      />
    </div>
  )
}

interface ScrollableActionViewBodyProps {
  children: ReactNode
  scrollRef: (node: HTMLDivElement | null | undefined) => void
  scrollElRef: { current: HTMLDivElement | null }
  onClick: (event: ReactMouseEvent<HTMLDivElement>) => void
  canEditContent: boolean
  isUpdating: boolean
}

function ScrollableActionViewBody({
  children,
  scrollRef,
  scrollElRef,
  onClick,
  canEditContent,
  isUpdating,
}: ScrollableActionViewBodyProps) {
  const paddingBottom = useViewBodyBottomInset()
  const bottomChrome = useViewBodyBottomChrome()
  useViewEntryScrollPersistence(scrollElRef)

  return (
    <FadingScrollable
      ref={scrollRef}
      fadeSize="3%"
      bottomChrome={bottomChrome}
      onClick={onClick}
      className={twMerge(
        // The overlay page renders in quirks mode, where the editor's `h-full` resolves against this
        // scroller and clamps it to one screen; the overflow then lands past the bottom inset (TRU-49).
        'min-h-0 flex-1 px-4 [&_[data-slot=markdown-editable]]:h-auto',
        canEditContent && 'cursor-text',
        // Regeneration blocks edits without blurring scrolling text.
        isUpdating && 'pointer-events-none opacity-25',
      )}
      style={{ paddingBottom }}
    >
      {children}
    </FadingScrollable>
  )
}

interface ActionViewContentProps {
  /** Remount key — forces a fresh editor when the gated feature set changes. */
  editorKey: string
  value: string
  editable: boolean
  onChange?: (markdown: string, editor: { isFocused: boolean }) => void
  features?: MarkdownFeatures
  maxLength?: number
  attachments?: NonNullable<NonNullable<ActionViewModel['action']>['view']>['attachments']
  /** Land with the caret at the end of the body (hover card's edit affordance). */
  autoFocus?: boolean | 'end'
  /** Fired once the editor has been created with `autoFocus` — the flag is spent. */
  onAutoFocusApplied?: () => void
}

/**
 * The draft content, rendered chromeless on the surface. Always editable on a
 * staged action — no edit mode. The controlled `value` (draft overlay first)
 * is the no-clobber guard: the editor skips external syncs while focused, and
 * a dirty draft pins `value` to the user's markdown, so a regeneration landing
 * mid-edit can't repaint over unsent edits.
 */
function ActionViewContent({
  editorKey,
  value,
  editable,
  onChange,
  features,
  maxLength,
  attachments,
  autoFocus,
  onAutoFocusApplied,
}: ActionViewContentProps) {
  // Tiptap reads `autoFocus` at creation only, so the mount that saw it set is
  // the one that spent it — report back once the editor exists rather than from
  // the body, which renders long before it (edit options loading, and the editor
  // itself is lazy-loaded, so its first mount in a window shows a read-only fallback).
  const [editor, setEditor] = useState<Editor | null>(null)
  const reportedRef = useRef(false)
  useEffect(() => {
    if (!autoFocus || !editor || reportedRef.current) return
    reportedRef.current = true
    onAutoFocusApplied?.()
  }, [autoFocus, editor, onAutoFocusApplied])

  return (
    <StagedActionMarkdown
      key={editorKey}
      value={value}
      editable={editable}
      onChange={onChange}
      features={features}
      maxLength={maxLength}
      placeholder="Edit content…"
      attachments={attachments}
      autoFocus={autoFocus}
      editorRef={setEditor}
    />
  )
}

// Shown while edit options resolve — mounting the editor first would flash the
// content with every feature on, then remount with the gated set.
function ActionContentSkeleton() {
  return (
    <div className="space-y-2 py-1" aria-hidden>
      <div className="bg-skeleton corner-squircle h-3.5 w-full animate-pulse rounded" />
      <div className="bg-skeleton corner-squircle h-3.5 w-11/12 animate-pulse rounded" />
      <div className="bg-skeleton corner-squircle h-3.5 w-4/5 animate-pulse rounded" />
    </div>
  )
}

function UndraftedNotice() {
  return (
    <div className="mb-2 flex items-start gap-2 rounded-xl border border-dashed p-3">
      <PenLine aria-hidden="true" className="text-foreground-secondary mt-0.5 size-3.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-foreground text-sm font-medium">Not drafted yet</p>
        <p className="text-foreground-secondary text-sm">
          Draft it from the meeting, or type below to write it yourself.
        </p>
      </div>
    </div>
  )
}
